import websocketConfig from '../config/websocketConfig';
import { sessionIdUtils, transformers } from './serviceUtils';
import { Message } from '../chatTypes';
import { getUserId } from '../chatUtils';
import { showNotification } from '../../../../../utils/notificationUtils';

// Define base WebSocket message interface
interface WebSocketMessageBase {
    [key: string]: unknown;
}

interface WebSocketMessageWithType extends WebSocketMessageBase {
    type: string;
}

type WebSocketMessage = WebSocketMessageBase | WebSocketMessageWithType;

type WebSocketEventHandler = (data: WebSocketMessage) => void;
type MessageCallback = (message: Message) => void;
type StatusCallback = (status: boolean) => void;
type ErrorCallback = (error: Error) => void;

// Define new WebSocket message interface for AgentSubUserChat
interface AgentSubUserChatMessage {
    msg: string;
    agentUniqId: number;
    agentId: string;
    subUserName: string;
    type: string;
    subUserId: number;
    userId: number;
}

/**
 * WebSocket connection states
 */
export enum WebSocketState {
    CONNECTING = 'CONNECTING',
    OPEN = 'OPEN',
    CLOSING = 'CLOSING',
    CLOSED = 'CLOSED',
}

// Define WebSocket events from config
export const WS_EVENTS = {
    WELCOME: 'welcome',
    TEXT: 'text',
    ...websocketConfig.events
};

// Update the LegacyMessage interface to include the new message format
interface LegacyMessage {
    type: string | { type: string };
    message?: { text: string };
    agentId?: { agentId: string };
    agentName?: string;
    fileName?: { fileName: string };
    // Add fields for new message format
    msg?: string;
    agentUniqId?: number;
    subUserName?: string;
    subUserId?: number;
    userId?: number;
}

/**
 * WebSocket service for real-time chat messaging - simplified to only receive messages
 */
class WebSocketService {
    private socket: WebSocket | null = null;
    private reconnectAttempts = 0;
    private reconnectTimer: NodeJS.Timeout | null = null;
    private heartbeatTimer: NodeJS.Timeout | null = null;
    private eventHandlers: Map<string, WebSocketEventHandler[]> = new Map();
    private userId: string = '';
    private connected = false;
    private agentConnected = false;  // Track agent connection state locally

    /**
     * Initialize the WebSocket connection
     */
    public connect(userId: string): Promise<void> {
        console.log('connecting socket for ' + userId);
        this.userId = userId;

        return new Promise((resolve, reject) => {
            if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
                resolve();
                return;
            }

            try {
                // Clear any existing reconnect timers
                if (this.reconnectTimer) {
                    clearTimeout(this.reconnectTimer);
                    this.reconnectTimer = null;
                }

                // Create WebSocket connection
                const url = websocketConfig.baseUrl;
                this.socket = new WebSocket(url);

                // Setup event listeners
                this.socket.onopen = (socketEvent) => {
                    console.log('WebSocket connection opened:', socketEvent);
                    this.connected = true;
                    this.reconnectAttempts = 0;
                    // this.startHeartbeat();
                    this.emitEvent(websocketConfig.events.connect, { userId });

                    // Send initial connection message (required for socket initialization)
                    const sessionId = sessionIdUtils.getSessionId();
                    if (sessionId) {
                        this.send({
                            sid: sessionId,
                            action: 'onSocketConnection',
                        });
                    }

                    resolve();
                };

                this.socket.onmessage = (event) => {
                    console.log('WebSocket message received:', event);
                    try {
                        let data;
                        try {
                            data = JSON.parse(event.data);
                        } catch {
                            console.log('Error parsing WebSocket message:', event.data);
                        }

                        // Handle system messages
                        if (data.type === 'pong') {
                            // Heartbeat pong received
                            return;
                        }

                        // Handle legacy messages using the old format's handler
                        this.handleSocketMessage(data);

                        // Handle incoming messages based on event type
                        if (data.type) {
                            this.emitEvent(data.type, data);
                        }

                        // Always emit generic message event
                        this.emitEvent(websocketConfig.events.message, data);
                    } catch (error) {
                        console.error('Error parsing WebSocket message:', error);
                    }
                };

                this.socket.onclose = (event) => {
                    this.connected = false;
                    this.stopHeartbeat();

                    if (websocketConfig.reconnection.autoReconnect) {
                        this.userId = getUserId() || '';
                        if (this.userId) {
                            this.scheduleReconnect();
                        }
                    }

                    this.emitEvent(websocketConfig.events.disconnect, {
                        code: event.code,
                        reason: event.reason,
                        wasClean: event.wasClean
                    });
                };

                this.socket.onerror = () => {
                    // Convert the Event to a WebSocketMessage format
                    const errorMessage: WebSocketMessage = {
                        type: 'error',
                        error: 'WebSocket connection error'
                    };
                    this.emitEvent(websocketConfig.events.error, errorMessage);
                    reject(new Error('WebSocket connection error'));
                };
            } catch (error) {
                console.error('WebSocket connection error:', error);
                if (websocketConfig.reconnection.autoReconnect) {
                    this.scheduleReconnect();
                }
                reject(error);
            }
        });
    }

    // Update the handleSocketMessage method to handle the new message format
    private handleSocketMessage(message: LegacyMessage): void {
        if (message && message.type) {
            const messageType = typeof message.type === 'object' ? message.type.type : message.type;

            if (!this.agentConnected && message.agentId) {
                const agentName = message.agentName || message?.agentId.agentId || "";
                this.onAgentConnected(agentName);
            }

            if (messageType === WS_EVENTS.WELCOME) {
                // Handle welcome message
            } else if (messageType === WS_EVENTS.TEXT && message.message) {
                this.onAgentTextReceived('agent', message.message.text);
            } else if (messageType === 'disconnect') {
                this.onReceiveDisconnect(true, message);
            } else if (messageType === "agentConnected") {
                if (!this.agentConnected && message.message) {
                    this.onAgentConnected(message.message.text);
                }
            } else if (messageType === "AgentSubUserChat" && message.msg) {
                // Handle the new message format
                this.emitEvent(websocketConfig.events.newMessage, message as unknown as WebSocketMessage);
            } else if (messageType === websocketConfig.events.closeSocket) {
                this.disconnect();
            } else {
                this.handleDefaultMessage(message);
            }
        }
    }

    // Handle default message types (for backward compatibility)
    private handleDefaultMessage(message: LegacyMessage): void {
        if (message.type !== undefined) {
            console.log("Unhandled message type: " + (typeof message.type === 'object' ? message.type.type : message.type));
        }
    }

    // Legacy event handler methods
    private onAgentConnected(agentName: string): void {
        this.agentConnected = true;
        this.emitEvent('agentConnected', { agentName });
    }

    private onAgentTextReceived(sender: string, text: string): void {
        this.emitEvent('agentMessage', { sender, text });
    }

    private onReceiveDisconnect(isDisconnected: boolean, message: LegacyMessage): void {
        if (isDisconnected) {
            this.agentConnected = false;
        }
        this.emitEvent('agentDisconnect', { isDisconnected, message });
    }

    // Check if agent is connected
    public isAgentConnected(): boolean {
        return this.agentConnected;
    }

    /**
     * Disconnect the WebSocket
     */
    public disconnect(): void {
        console.log('disconnect');
        if (this.socket) {
            this.stopHeartbeat();
            console.log('stopHeartbeat');
            if (this.reconnectTimer) {
                clearTimeout(this.reconnectTimer);
                this.reconnectTimer = null;
            }
            console.log('clearTimeout');
            // Close the connection
            if (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING) {
                this.socket.close();
                console.log('closing socket for ' + this.socket);
            }

            this.socket = null;
            this.connected = false;
        }
    }

    /**
     * Get the current WebSocket state
     */
    public getState(): WebSocketState {
        if (!this.socket) return WebSocketState.CLOSED;

        switch (this.socket.readyState) {
            case WebSocket.CONNECTING:
                return WebSocketState.CONNECTING;
            case WebSocket.OPEN:
                return WebSocketState.OPEN;
            case WebSocket.CLOSING:
                return WebSocketState.CLOSING;
            case WebSocket.CLOSED:
            default:
                return WebSocketState.CLOSED;
        }
    }

    /**
     * Subscribe to WebSocket events
     */
    public on(event: string, handler: WebSocketEventHandler): () => void {
        if (!this.eventHandlers.has(event)) {
            this.eventHandlers.set(event, []);
        }

        this.eventHandlers.get(event)?.push(handler);

        // Return unsubscribe function
        return () => {
            const handlers = this.eventHandlers.get(event) || [];
            this.eventHandlers.set(
                event,
                handlers.filter((h) => h !== handler)
            );
        };
    }

    /**
     * Subscribe to new message events
     */
    public onNewMessage(callback: MessageCallback): () => void {
        return this.on(websocketConfig.events.newMessage, (data) => {
            // Handle AgentSubUserChat format
            if (data && typeof data === 'object' && 'type' in data && data.type === 'AgentSubUserChat' && 'msg' in data) {
                const agentMessage = data as unknown as AgentSubUserChatMessage;
                const message: Message = {
                    id: `msg-${agentMessage.agentUniqId}-${Date.now()}`,
                    senderId: agentMessage.agentUniqId.toString(),
                    senderName: agentMessage.agentId,
                    content: agentMessage.msg,
                    timestamp: new Date(),
                    isRead: false,
                    isSent: true,
                };

                // Show notification with sender name and message
                showNotification(message.senderName, {
                    body: message.content,
                    tag: `message-${message.id}`
                });

                callback(message);
            }
            // Handle legacy message format
            else if (data && typeof data === 'object' && 'message' in data && data.message) {
                // First convert to unknown then to Message to satisfy TypeScript
                const parsedData = transformers.parseDates(data.message as Record<string, unknown>, ['timestamp']);
                const message = parsedData as unknown as Message;

                // Show notification with sender name and message
                if (message.senderName && message.content) {
                    showNotification(message.senderName, {
                        body: message.content,
                        tag: `message-${message.id}`
                    });
                }

                callback(message);
            }
        });
    }

    /**
     * Subscribe to connection status changes
     */
    public onConnectionChange(callback: StatusCallback): void {
        this.on(websocketConfig.events.connect, () => callback(true));
        this.on(websocketConfig.events.disconnect, () => callback(false));
    }

    /**
     * Subscribe to errors
     */
    public onError(callback: ErrorCallback): () => void {
        // Create a wrapper that converts WebSocketMessage to Error
        const errorHandler: WebSocketEventHandler = (data) => {
            // If data is already an Error, pass it directly
            if (data instanceof Error) {
                callback(data);
                return;
            }

            // Otherwise create an Error from the data
            const error = new Error(
                typeof data === 'object' && data && 'message' in data
                    ? String(data.message)
                    : 'Unknown error'
            );
            callback(error);
        };

        return this.on(websocketConfig.events.error, errorHandler);
    }

    /**
     * Is the WebSocket currently connected
     */
    public isConnected(): boolean {
        return this.connected && this.socket?.readyState === WebSocket.OPEN;
    }

    /**
     * Send data through WebSocket - minimized to only required functionality
     */
    private send(data: WebSocketMessage): boolean {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            this.socket.send(JSON.stringify(data));
            return true;
        }
        return false;
    }

    /**
     * Emit an event to all registered handlers
     */
    private emitEvent(event: string, data: WebSocketMessage): void {
        const handlers = this.eventHandlers.get(event) || [];
        handlers.forEach(handler => {
            try {
                handler(data);
            } catch (error) {
                console.error(`Error in ${event} event handler:`, error);
            }
        });
    }

    /**
     * Schedule a reconnection attempt
     */
    private scheduleReconnect(): void {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
        }

        if (this.reconnectAttempts < websocketConfig.reconnection.maxRetries) {
            const delay = websocketConfig.reconnection.retryInterval *
                Math.pow(websocketConfig.reconnection.backoffMultiplier, this.reconnectAttempts);

            this.reconnectAttempts++;

            this.emitEvent(websocketConfig.events.reconnectAttempt, {
                attempt: this.reconnectAttempts,
                maxRetries: websocketConfig.reconnection.maxRetries
            });

            this.reconnectTimer = setTimeout(() => {
                this.connect(this.userId)
                    .then(() => {
                        this.emitEvent(websocketConfig.events.reconnect, {
                            attempt: this.reconnectAttempts
                        });
                    })
                    .catch(error => {
                        console.error('Reconnection failed:', error);
                    });
            }, delay);
        }
    }

    /**
     * Start heartbeat to keep connection alive
     */
    private startHeartbeat(): void {
        if (websocketConfig.heartbeat.enabled && !this.heartbeatTimer) {
            this.heartbeatTimer = setInterval(() => {
                if (this.isConnected()) {
                    this.send(websocketConfig.heartbeat.payload);
                }
            }, websocketConfig.heartbeat.interval);
        }
    }

    /**
     * Stop the heartbeat
     */
    private stopHeartbeat(): void {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }
}

// Create a singleton instance
const websocketService = new WebSocketService();

export default websocketService; 